import React, { useEffect, useState } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import {
  Field,
  Radio,
  RadioGroup,
  FluentProvider,
  webLightTheme,
  Textarea,
  Input,
  Select,
} from '@fluentui/react-components';
import { Controller, UseFormMethods } from 'react-hook-form';
import InlineFormInputErrorMessage from '../../../common/errorContent/InlineFormInputErrorMessage';
import UtcDatePicker from '../../../common/utcDatePicker/UtcDatePicker';
import CurrencyCodeDropdown from '../../../common/formFields/CurrencyCodeDropdown';
import MultiSelectDropdown from '../../../common/MultiSelectDropdown';
import {
  paperSizeOptions,
  signatureInkColorOptions,
  yesOption,
  noOption,
  additionalProcessingTimeOptions,
} from '../../../../utils/types/CommonDropdownOptions';
import ICompanyDefaultAgmDetails from '../../../../utils/types/ICompanyDefaultAgmDetails';

interface ICompanyDefaultAgmDetailsFormFormProps {
  form: UseFormMethods<ICompanyDefaultAgmDetails>;
  data: ICompanyDefaultAgmDetails;
}

const CompanyDefaultAgmDetailsForm = ({
  form,
  data,
}: ICompanyDefaultAgmDetailsFormFormProps): JSX.Element => {
  const { errors, control, setValue, watch } = form;

  const [selectedAdditionalTimeOptions, setSelectedAdditionalTime] = useState<string[]>(() => {
    const values: string[] = [];
    let isDirty = false;
    if (data?.additionalProcessingTimeApostille) {
      values.push('APOSTILLE');
      isDirty = true;
    }
    if (data?.additionalProcessingTimeLegalization) {
      values.push('LEGALIZATION');
      isDirty = true;
    }
    if (data?.additionalProcessingTimeLocalSigner) {
      values.push('LOCALSIGNER');
      isDirty = true;
    }
    if (isDirty) {
      setValue('additionalProcessingTime', values, { shouldDirty: true });
    }
    return values;
  });

  useEffect(() => {
    setValue('additionalProcessingTime', selectedAdditionalTimeOptions);
  }, [selectedAdditionalTimeOptions, setValue]);

  const watchTaxDeadlineBeforeAfsDeadline = watch(
    'taxFilingDeadlineBeforeAfsDeadline',
    data?.taxFilingDeadlineBeforeAfsDeadline,
  );

  const watchSignatureRequiredDeadline = watch(
    'physicalSignatureRequired',
    data?.physicalSignatureRequired,
  );

  const watchInkColorRequired = watch('inkColorRequired', data?.inkColorRequired);

  const watchPaperSize = watch('paperSize', data?.paperSize);

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 5 }}>
        <Controller
          name="agmDeadlineBeforeAfsDeadline"
          defaultValue={data?.agmDeadlineBeforeAfsDeadline}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <>
                <Field label="Is the AGM deadline earlier than the AFS deadline?">
                  <RadioGroup
                    id="agmDeadlineBeforeAfsDeadline"
                    name="agmDeadlineBeforeAfsDeadline"
                    value={value?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      setValue('agmDeadlineBeforeAfsDeadline', radioData.value === 'true', {
                        shouldDirty: true,
                      });
                      if (radioData.value === 'false') {
                        setValue('agmDeadline', null, { shouldDirty: true });
                      }
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage
                  errorMessage={errors?.agmDeadlineBeforeAfsDeadline?.message}
                />
              </>
            );
          }}
        />
        <Controller
          name="taxFilingDeadlineBeforeAfsDeadline"
          defaultValue={data?.taxFilingDeadlineBeforeAfsDeadline}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <>
                <Field label="Is the tax filing deadline earlier than the AFS deadline?">
                  <RadioGroup
                    id="taxFilingDeadlineBeforeAfsDeadline"
                    name="taxFilingDeadlineBeforeAfsDeadline"
                    value={value?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      setValue('taxFilingDeadlineBeforeAfsDeadline', radioData.value === 'true', {
                        shouldDirty: true,
                      });
                      if (radioData.value === 'false') {
                        setValue('taxFilingDeadline', null, { shouldDirty: true });
                      }
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage
                  errorMessage={errors?.taxFilingDeadlineBeforeAfsDeadline?.message}
                />
              </>
            );
          }}
        />
        <Controller
          name="taxFilingDeadline"
          control={control}
          defaultValue={data?.taxFilingDeadline}
          render={({ value }): React.ReactElement => (
            <div role="form">
              <UtcDatePicker
                label="Tax Filing Deadline"
                disabled={watchTaxDeadlineBeforeAfsDeadline !== true}
                allowTextInput
                disableAutoFocus={false}
                value={value}
                textField={{
                  name: 'taxFilingDeadline',
                  errorMessage: errors?.taxFilingDeadline?.message,
                }}
                onSelectDate={(utcDateString: string): void => {
                  setValue('taxFilingDeadline', utcDateString, {
                    shouldDirty: value !== utcDateString,
                  });
                }}
              />
            </div>
          )}
        />
        <Controller
          name="externalAfsFilingRequired"
          defaultValue={data?.externalAfsFilingRequired}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <>
                <Field label="Is there a formal AFS external filing required?">
                  <RadioGroup
                    id="externalAfsFilingRequired"
                    name="externalAfsFilingRequired"
                    value={value?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      setValue('externalAfsFilingRequired', radioData.value === 'true', {
                        shouldDirty: true,
                      });
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage
                  errorMessage={errors?.externalAfsFilingRequired?.message}
                />
              </>
            );
          }}
        />
        <Controller
          name="requiredToBeFiledWithAfs"
          defaultValue={data?.requiredToBeFiledWithAfs}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <>
                <Field label="Do the AGM date and the FS date have to be aligned?">
                  <RadioGroup
                    id="requiredToBeFiledWithAfs"
                    name="requiredToBeFiledWithAfs"
                    value={value?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      setValue('requiredToBeFiledWithAfs', radioData.value === 'true', {
                        shouldDirty: true,
                      });
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage
                  errorMessage={errors?.requiredToBeFiledWithAfs?.message}
                />
              </>
            );
          }}
        />
        <Controller
          name="canAfsBeExtended"
          defaultValue={data?.canAfsBeExtended}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <>
                <Field label="Can the AFS deadline be extended?">
                  <RadioGroup
                    id="canAfsBeExtended"
                    name="canAfsBeExtended"
                    value={value?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      setValue('canAfsBeExtended', radioData.value === 'true', {
                        shouldDirty: true,
                      });
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage errorMessage={errors?.canAfsBeExtended?.message} />
              </>
            );
          }}
        />
        <Controller
          name="physicalSignatureRequired"
          defaultValue={data?.physicalSignatureRequired}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <>
                <Field label="Is a hard copy signature required for filing?">
                  <RadioGroup
                    id="physicalSignatureRequired"
                    name="physicalSignatureRequired"
                    value={value?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      setValue('physicalSignatureRequired', radioData.value === 'true', {
                        shouldDirty: true,
                      });
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage
                  errorMessage={errors?.physicalSignatureRequired?.message}
                />
              </>
            );
          }}
        />
        <Controller
          name="hardCopyShippingAddress"
          control={control}
          defaultValue={data?.hardCopyShippingAddress}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="Address for Hard Copies"
              validationMessage={errors?.hardCopyShippingAddress?.message}
            >
              <Textarea
                id="hardCopyShippingAddress"
                disabled={watchSignatureRequiredDeadline !== true}
                value={value || ''}
                onChange={(e, inputData) => onChange(inputData.value)}
                name={name}
                resize="none"
              />
            </Field>
          )}
        />
        <Controller
          name="inkColorRequired"
          defaultValue={data?.inkColorRequired}
          control={control}
          render={() => {
            return (
              <>
                <Field label="Is there a required ink color for the signatures?">
                  <RadioGroup
                    id="inkColorRequired"
                    name="inkColorRequired"
                    value={watchInkColorRequired?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      setValue('inkColorRequired', radioData.value === 'true', {
                        shouldDirty: true,
                      });
                      if (radioData.value === 'false') {
                        setValue('signatureInkColor', null);
                      }
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage errorMessage={errors?.inkColorRequired?.message} />
              </>
            );
          }}
        />
        <Controller
          name="signatureInkColor"
          control={control}
          defaultValue={data?.signatureInkColor}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <Field
              label="Signature Ink Color"
              validationMessage={errors?.signatureInkColor?.message}
            >
              <Select
                onChange={(event, selected): void => {
                  setValue('signatureInkColor', selected.value, { shouldDirty: true });
                }}
                defaultValue={data?.signatureInkColor ?? ''}
                disabled={watchInkColorRequired !== true}
                placeholder="Select an option"
              >
                <option value="" disabled>
                  Select an option
                </option>
                {signatureInkColorOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.text}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        />
        <Controller
          name="originalsCount"
          control={control}
          defaultValue={data?.originalsCount?.toString()}
          rules={{
            min: {
              value: 0,
              message: 'Please enter a positive number',
            },
          }}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="How many originals are required"
              validationMessage={errors?.originalsCount?.message}
            >
              <Input
                name={name}
                type="number"
                value={value}
                onChange={(e, inputData) => onChange(inputData.value)}
              />
            </Field>
          )}
        />
        <Controller
          name="paperSize"
          control={control}
          defaultValue={data?.paperSize}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <Field label="Paper Size" validationMessage={errors?.paperSize?.message}>
              <Select
                onChange={(event, selected): void => {
                  setValue('paperSize', selected.value, { shouldDirty: true });
                  if (selected.value !== 'OTHER') {
                    setValue('otherPaperSize', '', { shouldDirty: true });
                  }
                }}
                defaultValue={data?.paperSize}
                placeholder="Select an option"
              >
                {paperSizeOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.text}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        />
        <Controller
          name="otherPaperSize"
          control={control}
          defaultValue={data?.otherPaperSize}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field label="Other Paper Size" validationMessage={errors?.otherPaperSize?.message}>
              <Textarea
                id="otherPaperSize"
                disabled={watchPaperSize !== 'OTHER'}
                value={value || ''}
                onChange={(e, inputData) => onChange(inputData.value)}
                name={name}
                resize="none"
              />
            </Field>
          )}
        />
        <Controller
          name="managementRepresentationLetterRequired"
          defaultValue={data?.managementRepresentationLetterRequired}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <>
                <Field label="Is a Management Representation Letter Required?">
                  <RadioGroup
                    id="managementRepresentationLetterRequired"
                    name="managementRepresentationLetterRequired"
                    value={value?.toString().toLowerCase()}
                    onChange={(e, radioData): void => {
                      setValue(
                        'managementRepresentationLetterRequired',
                        radioData.value === 'true',
                        {
                          shouldDirty: true,
                        },
                      );
                    }}
                  >
                    <Radio value={yesOption.key} label={yesOption.text} />
                    <Radio value={noOption.key} label={noOption.text} />
                  </RadioGroup>
                </Field>
                <InlineFormInputErrorMessage
                  errorMessage={errors?.managementRepresentationLetterRequired?.message}
                />
              </>
            );
          }}
        />
        <Controller
          name="auditorFeesCurrencyCode"
          control={control}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <CurrencyCodeDropdown
              propertyName="auditorFeesCurrencyCode"
              label="Auditor Currency"
              handleChange={(newValue: string, valueKey: string): void => {
                setValue('auditorFeesCurrencyCode', newValue, { shouldDirty: true });
              }}
              defaultValue={data?.auditorFeesCurrencyCode}
              errorMessage={errors?.auditorFeesCurrencyCode?.message}
            />
          )}
          defaultValue={data?.auditorFeesCurrencyCode}
        />
        <Controller
          name="additionalProcessingTime"
          control={control}
          render={() => {
            return (
              <MultiSelectDropdown
                label="Additional Processing Time"
                onChange={(event, selectedItems): void => {
                  const selectedKeys = selectedItems.map((item) => item.key.toString());
                  setSelectedAdditionalTime(selectedKeys);
                  setValue('additionalProcessingTime', selectedKeys, { shouldDirty: true });
                }}
                options={additionalProcessingTimeOptions}
                selectedKeys={selectedAdditionalTimeOptions}
                placeholder="Select an option"
                errorMessage={errors?.additionalProcessingTimeApostille?.message}
              />
            );
          }}
        />
      </Stack>
    </FluentProvider>
  );
};

export default CompanyDefaultAgmDetailsForm;
